import { z } from 'zod';

import type { Translate } from '../../../target/build/shared/source/internationalization/translation';

export const careerLevelSchema = z.object({
    description: z.string(),
    level: z.number().int().nonnegative(),
    title: z.string(),
});

export type CareerLevel = z.infer<typeof careerLevelSchema>;

export const tradeUnionTypeSchema = z.union([
    z.literal('djv'),
    z.literal('evg'),
    z.literal('gdl'),
    z.literal('gew'),
    z.literal('ig_bau'),
    z.literal('ig_bce'),
    z.literal('ig_metall'),
    z.literal('marburger_bund'),
    z.literal('market_based'),
    z.literal('ngg'),
    z.literal('ufo'),
    z.literal('verdi'),
]);

export type TradeUnionType = z.infer<typeof tradeUnionTypeSchema>;

export type UnionBasedTradeUnionType = Exclude<TradeUnionType, 'market_based'>;

export const careerTrackSchema = z.object({
    id: z.uuid(),
    careerLevels: careerLevelSchema.array(),
    description: z.string().optional(),
    title: z.string(),
    type: tradeUnionTypeSchema,
});

export const addTradeUnionResponseSchema = z
    .object({
        id: z.uuid(),
        careerLevels: z
            .object({
                description: z.string().optional(),
                level: z.number().int(),
                title: z.string().optional(),
            })
            .array(),
        description: z.string().optional(),
        title: z.string(),
        type: tradeUnionTypeSchema,
    })
    .array();

export type AddTradeUnionResponse = z.infer<typeof addTradeUnionResponseSchema>;

type CareerTrackTransformationInput = z.input<typeof careerTrackSchema>;

export type TradeUnionWithTranslation = {
    readonly id: TradeUnionType;
    readonly name: string;
};

type CareerTrackTransformationOutput = CareerTrackTransformationInput & {
    readonly trackType: TradeUnionWithTranslation;
};

export const createTradeUnionWithTranslation = (
    translate: Translate,
    tradeUnionType: TradeUnionType,
): TradeUnionWithTranslation => {
    return {
        id: tradeUnionType,
        name: translate(`pages.job-architecture.trade-union.${tradeUnionType}`),
    };
};

export const createTradeUnionsWithTranslation = (translate: Translate): readonly TradeUnionWithTranslation[] => {
    return tradeUnionTypeSchema.options.map((option) => createTradeUnionWithTranslation(translate, option.value));
};

export const createCareerTrackWithTradeUnionsSchema = (
    translate: Translate,
): z.ZodType<CareerTrackTransformationOutput, CareerTrackTransformationInput> => {
    return careerTrackSchema.transform(({ type, ...rest }) => ({
        ...rest,
        trackType: createTradeUnionWithTranslation(translate, type),
        type,
    }));
};

export type CareerTrack = z.infer<ReturnType<typeof createCareerTrackWithTradeUnionsSchema>>;
